/**
 * `/posts/:id` — модерация одной публикации: куда (аккаунты), тексты по сетям, рамки, превью, утверждение.
 *
 * Черновик сохраняется сам: правки копятся поверх ответа сервера (`lib/postDraft.ts`) и уходят `PATCH`
 * очередью — один запрос в полёте, ввод с задержкой 1 с, галочки и рамка сразу; `base_modify` следующего —
 * `date_modify` последнего ответа. Ответы мутаций без CRM — через `mergeDetail`. 409 `STALE` — плашка
 * «перечитать» (свежая публикация из тела ошибки, свои несохранённые правки сбрасываются).
 * Генерация — ТОЛЬКО кнопкой (платно): сначала очередь сохранения сбрасывается, потом вызов; на время поля
 * неактивны; ошибка или обрыв — перечитать публикацию (генерации могли записаться).
 *
 * Утверждают СЕТЬ, а не публикацию (№285): «Утвердить ‹Сеть›», «Вернуть в черновик» и «Не публиковать
 * в ‹Сеть›» живут в карточке сети (`NetworkCard`) и уходят с `networks: [код]`, причины отказа —
 * только этой сети. «Только чтение» тоже на сеть: утверждённая заморожена, соседняя правится. Общие
 * части (состав карусели, платье = обложка) замораживаются целиком, пока утверждена хоть одна сеть, —
 * полоса «Что публикуем» прямо говорит, какую сеть вернуть в черновик. Удалить черновик (отменить
 * публикацию целиком) — кнопка в шапке справа (№378: с плитки списка удаление убрано), после — в список.
 * Кнопка генерации текста одна — в карточке сети (№379); над карточкой только подсказка, когда карточки нет.
 *
 * Раскладка по макетам Оли (№383): вкладки сетей сразу под шапкой, под ними аккаунты; слева карточка
 * сети «Предпросмотр публикации» (одна картинка с рамкой, карусель под ней), справа блок «Товар». Дата
 * выкладки — в блоке утверждения карточки, «Запланировать…» с календарём сети (№385, `schedulePlan`;
 * правится и у утверждённой сети).
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";

import { AccountPicker } from "@/components/posts/AccountPicker";
import { CandidatePicker } from "@/components/posts/CandidatePicker";
import { CarouselBar, NetworkCard, type CarouselProps } from "@/components/posts/NetworkCard";
import { btn, errorText, ErrorLine, Loading } from "@/components/trends/ui";
import { ApiFailure, type ApiErrorItem } from "@/lib/api";
import { num, when } from "@/lib/media";
import { boundSeriesNote, modelLabel } from "@/lib/post";
import {
  applyEdits,
  defaultNet,
  pinnedNet,
  isEmpty,
  mergeEdits,
  netCounts,
  noEdits,
  patchBody,
  settle,
  soleAccount,
  type Edits,
  type NetEdit,
} from "@/lib/postDraft";
import { move } from "@/lib/postMedia";
import {
  approveProblems,
  deletePlan,
  mergeDetail,
  netApproveProblems,
  netFrozen,
  NETWORK_LABEL,
  networkNames,
  NETWORKS,
  partialNote,
  type Crop,
  type PostDetail,
  type PostNetworkCode,
  type PostProblems,
} from "@/lib/postPlan";
import { SaveQueue, type SaveFailure } from "@/lib/saveQueue";
import {
  addMedia,
  approvePlan,
  cancelPlan,
  schedulePlan,
  generatePlan,
  loadPlan,
  patchPlan,
  postsKeys,
  removeMedia,
  reorderMedia,
  setMediaCrop,
  takeGen,
  unapprovePlan,
} from "@/lib/postsApi";

type ErrorExtra = ApiErrorItem & { plan?: PostDetail; problems?: PostProblems };

const STATUS_LABEL: Record<PostDetail["status"], string> = {
  draft: "Черновик",
  approved: "Утверждено",
  cancelled: "Отменено",
};

/** Статус СЕТИ (№285) — подпись на её вкладке. */
const NET_STATUS_LABEL: Record<PostDetail["status"], string> = {
  draft: "черновик",
  approved: "утверждено",
  cancelled: "отменена",
};

const GEN_TEXT: Record<string, string> = {
  BUSY: "генерация уже идёт",
  NO_OPENROUTER_KEY: "нет ключа OpenRouter",
  BAD_NETWORK: "не отмечено ни одной сети в черновике",
  NOT_DRAFT: "сеть уже утверждена — верните её в черновик",
};

/** Отказ PATCH 4xx: правка отброшена, публикация перечитана — что сказать человеку. */
const REJECT_TEXT: Record<string, string> = {
  BAD_ACCOUNT: "аккаунт в архиве — галочка снята",
  BAD_CROP: "рамка не принята — вернул сохранённую",
  BAD_HASHTAGS: "хэштеги не приняты — вернул сохранённые",
  BAD_NETWORK: "сеть не принята — вернул сохранённое",
  NOT_DRAFT: "правка не принята: утверждённое не меняется — перечитал",
};

const saveText = (e: unknown): string =>
  (e instanceof ApiFailure && REJECT_TEXT[e.code]) || errorText(e);

const extra = (e: unknown): ErrorExtra | null => (e instanceof ApiFailure ? (e.items[0] as ErrorExtra) : null);

/** Причины отказа перехода: у перехода СЕТИ — её причины (общее публикации плюс свои, без подписи
 * сети: они показываются в её карточке), у перехода публикации — всё с подписями сетей. */
const stateProblems = (found: PostProblems | undefined, code: PostNetworkCode | null): string[] => {
  if (!found) return [];
  if (code) return netApproveProblems(found, code);
  return [
    ...found.common,
    ...NETWORKS.flatMap((c) => (found.networks[c] ?? []).map((p) => `${NETWORK_LABEL[c]}: ${p}`)),
  ];
};

const noop = () => {};

/** Что сейчас занято: генерация сети, «взять вариант», смена статуса. */
type Busy = { kind: "generate"; code: string } | { kind: "take"; code: string } | { kind: "state" } | null;

function usePlanDraft(id: number) {
  const qc = useQueryClient();
  const key = postsKeys.plan(id);
  const q = useQuery({ queryKey: key, queryFn: () => loadPlan(id), refetchOnWindowFocus: false });

  const [base, setBase] = useState<PostDetail | null>(null);
  const baseRef = useRef<PostDetail | null>(null);
  const [local, setLocalState] = useState<Edits>(noEdits);
  const localRef = useRef<Edits>(noEdits());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<{ error: unknown; kind: SaveFailure } | null>(null);
  // 409 STALE: `plan` — свежая публикация из тела ошибки (может не прийти)
  const [stale, setStale] = useState<{ plan: PostDetail | null } | null>(null);

  const setLocal = (e: Edits) => {
    localRef.current = e;
    setLocalState(e);
  };

  const putBase = (next: PostDetail) => {
    const prev = baseRef.current;
    const merged = prev ? mergeDetail(prev, next) : next;
    baseRef.current = merged;
    setBase(merged);
  };

  /** Списки, метки «есть публикация» в окне выбора — всё, кроме самой публикации (её держим сами). */
  const invalidateOthers = () =>
    void qc.invalidateQueries({
      queryKey: postsKeys.all,
      predicate: (one) => !(one.queryKey[1] === "plan" && one.queryKey[2] === id),
    });

  /** Ответ мутации — новая база (и кэш, чтобы возврат на страницу не показал старое). */
  const accept = (next: PostDetail) => {
    putBase(next);
    qc.setQueryData(key, baseRef.current);
    invalidateOthers();
  };

  // GET (первый, «перечитать» после ошибки генерации) — только если не старее показанного:
  // ответ запроса, начатого до нашего PATCH, не должен откатить базу
  useEffect(() => {
    const d = q.data;
    if (!d) return;
    const prev = baseRef.current;
    if (!prev || d.date_modify >= prev.date_modify) putBase(d);
  }, [q.data]);

  const clearTimer = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };

  const markStale = (e: unknown): boolean => {
    const x = extra(e);
    if (x?.code !== "STALE") return false;
    clearTimer();
    // STALE от «взять вариант» / смены статуса: очередь тоже стоит — иначе drain пропустит платную генерацию
    queueRef.current?.markStale();
    setStale({ plan: x.plan ?? null });
    return true;
  };

  /** Отказ 4xx (кроме STALE): правка отброшена, база перечитана, остальная очередь уходит дальше. */
  const rejected = async (sent: Edits) => {
    setLocal(settle(localRef.current, sent));
    await refresh();
    if (queueRef.current?.pending) queueRef.current.flush().catch(noop);
  };

  const queueRef = useRef<SaveQueue<PostDetail> | null>(null);
  if (!queueRef.current) {
    queueRef.current = new SaveQueue<PostDetail>({
      patch: (body) =>
        patchPlan(id, { base_modify: (baseRef.current as PostDetail).date_modify, ...patchBody(body) }),
      onSaved: (next, sent) => {
        accept(next);
        setLocal(settle(localRef.current, sent));
        setSaveError(null);
      },
      onFailed: (e, kind, sent) => {
        if (kind === "stale") {
          markStale(e);
          return;
        }
        setSaveError({ error: e, kind });
        if (kind === "reject") void rejected(sent);
      },
      onSaving: setSaving,
      ready: () => baseRef.current !== null,
    });
  }
  const queue = queueRef.current;

  const flush = (): Promise<void> => {
    clearTimer();
    return queue.flush();
  };

  const drain = () => {
    clearTimer();
    return queue.drain();
  };

  const edit = (e: Edits, immediate: boolean) => {
    if (queue.stale || stale) return;
    setLocal(mergeEdits(localRef.current, e));
    queue.add(e);
    if (immediate) {
      flush().catch(noop);
    } else {
      clearTimer();
      timer.current = setTimeout(() => void flush().catch(noop), 1000);
    }
  };

  /** Взять свежую публикацию как базу; свои несохранённые правки сбрасываются. */
  const reread = (plan: PostDetail | null) => {
    clearTimer();
    queue.reset();
    setLocal(noEdits());
    setStale(null);
    setSaveError(null);
    if (plan) {
      const prev = baseRef.current;
      baseRef.current = prev ? mergeDetail(prev, plan) : plan;
      setBase(baseRef.current);
      qc.setQueryData(key, baseRef.current);
    }
    void q.refetch();
    invalidateOthers();
  };

  /** Перечитать после ошибки генерации или отказа PATCH: записанное сервером могло измениться. */
  const refresh = async () => {
    try {
      const fresh = await loadPlan(id);
      // PATCH, ушедший во время чтения, мог уже сдвинуть базу — старее не берём
      const prev = baseRef.current;
      if (prev && fresh.date_modify < prev.date_modify) return;
      putBase(fresh);
      qc.setQueryData(key, baseRef.current);
    } catch {
      // плашку ошибки уже показали; страница остаётся с прежней базой
    }
  };

  // уход со страницы: накопленное — отправить; закрытие вкладки с несохранённым — спросить
  useEffect(() => {
    const warn = (ev: BeforeUnloadEvent) => {
      if (queue.pending) ev.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => {
      window.removeEventListener("beforeunload", warn);
      if (queue.pending && !queue.stale) queue.flush().catch(noop);
    };
  }, []);

  return {
    q,
    base,
    baseRef,
    local,
    shown: base ? applyEdits(base, local) : null,
    saving,
    saveError,
    stale: stale !== null,
    stalePlan: stale?.plan ?? null,
    edit,
    flush,
    drain,
    accept,
    reread,
    refresh,
    markStale,
    setLocal,
    invalidateOthers,
  };
}

/** «Товар» (макет №383): артикул, привязка, модель, цена, города наличия. Картинки нет — кадр один, в карточке сети. */
function ProductPanel({ plan }: { plan: PostDetail }) {
  const d = plan.dress;
  const model = modelLabel(d.model_name);
  return (
    <div className="grid content-start gap-3 rounded-[var(--radius-card)] border border-border bg-bg p-3" data-annot="товар">
      <h2 className="text-[13px] font-medium text-fg">Товар</h2>
      {d.crm_error ? (
        <div className="rounded-[var(--radius-ui)] border border-warn/40 bg-warn-bg px-2 py-1.5 text-[12px] text-warn">
          {d.crm_error} — цены и городов нет.
        </div>
      ) : null}
      {d.series ? (
        <div className="grid gap-1 text-[12.5px]">
          <span className="font-medium text-fg">Арт. {d.series}</span>
          {boundSeriesNote(d.series, d.bound_series) ? (
            <span className="text-fg-faint">{boundSeriesNote(d.series, d.bound_series)}</span>
          ) : null}
          {model ? <span className="text-fg-muted">{model}</span> : null}
          {d.price_from !== null ? <span className="text-fg-muted">прокат от {num(d.price_from)} ₽</span> : null}
        </div>
      ) : (
        <div className="rounded-[var(--radius-ui)] border border-err/30 bg-err-bg px-2 py-1.5 text-[12px] text-err">
          Нет серии CRM — утвердить нельзя (артикул есть во всех текстах).{" "}
          {d.id !== null ? (
            <Link to={`/photos/${d.id}`} className="underline">
              Привязать в карточке платья
            </Link>
          ) : (
            "Фото не привязано к платью."
          )}
        </div>
      )}
      {d.cities !== null ? (
        <div className="grid gap-1">
          <span className="text-[11.5px] text-fg-faint">Города наличия</span>
          {d.cities.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {d.cities.map((c) => (
                <span
                  key={c.crm_location_id}
                  className="rounded-[var(--radius-ui)] bg-bg-subtle px-1.5 py-0.5 text-[11.5px] text-fg-muted"
                >
                  {c.name}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-[12px] text-fg-faint">нет в наличии</span>
          )}
        </div>
      ) : null}
    </div>
  );
}

function PlanScreen({ id }: { id: number }) {
  const draft = usePlanDraft(id);
  const navigate = useNavigate();
  const [busy, setBusy] = useState<Busy>(null);
  const [genError, setGenError] = useState<{ scope: string; text: string } | null>(null);
  const [genDone, setGenDone] = useState<{ code: string; text: string } | null>(null);
  const [kept, setKept] = useState<string[]>([]);
  const [takeError, setTakeError] = useState<{ code: string; text: string } | null>(null);
  // у перехода есть СЕТЬ (№285): отказ показывается в её карточке, а не в общем подвале
  const [stateError, setStateError] = useState<{ text: string; problems: string[]; net: PostNetworkCode | null } | null>(
    null,
  );
  const [addOpen, setAddOpen] = useState(false);
  // выбранный элемент карусели, когда карточки сети нет (у карточки выбор свой)
  const [loosePick, setLoosePick] = useState<number | null>(null);
  const [params, setParams] = useSearchParams();
  // вкладку фиксируем в адресе один раз на публикацию: правки аккаунтов не должны её переключать
  const pinnedFor = useRef<number | null>(null);
  const shownPlan = draft.shown;
  useEffect(() => {
    if (!shownPlan || pinnedFor.current === id) return;
    pinnedFor.current = id;
    const pin = pinnedNet(shownPlan, params.get("net"));
    if (pin) {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("net", pin);
          return next;
        },
        { replace: true },
      );
    }
  }, [id, shownPlan, params, setParams]);

  const { q, shown: plan } = draft;
  if (q.isError && !plan) return <ErrorLine error={q.error} />;
  if (!plan) return <Loading />;

  const isDraft = plan.status === "draft";
  const working = draft.stale || busy !== null;
  // Утверждённые сети: пока есть хоть одна, общие части публикации (состав карусели и платье обложки)
  // заморожены — сервер отвечает `NOT_DRAFT` с именем сети, и экран говорит то же заранее.
  const approvedNets = plan.approval.approved;
  const sharedReadOnly = !isDraft || working || approvedNets.length > 0;
  /** Поля, галочки и рамка СЕТИ: утверждённая и отменённая не правятся, соседние — правятся (№285). */
  const netReadOnly = (code: string) => !isDraft || working || netFrozen(plan.networks[code]);
  /** Переходы статуса: отменённая публикация их не принимает, а `approved` — принимает (возврат сети). */
  const stateBusy = plan.status === "cancelled" || working;
  const active = NETWORKS.filter((c) => plan.networks[c]?.active && plan.formats[c]);
  const problems = approveProblems(plan);
  const partial = partialNote(approvedNets, plan.approval.active);
  const tabs = plan.accounts.filter((n) => plan.formats[n.code]);
  const wanted = params.get("net");
  const net = tabs.some((n) => n.code === wanted) ? (wanted as string) : defaultNet(plan);
  const counts = netCounts(plan);
  const cards = active.filter((c) => c === net).flatMap((code) => {
    const net = plan.networks[code];
    const format = plan.formats[code];
    return net && format ? [{ code, net, format }] : [];
  });

  const generate = async (code: PostNetworkCode) => {
    const scope: string = code;
    setBusy({ kind: "generate", code });
    setGenError(null);
    setGenDone(null);
    try {
      await draft.drain();
    } catch (e) {
      setGenError({ scope, text: `черновик не сохранён: ${errorText(e)}` });
      setBusy(null);
      return;
    }
    try {
      const r = await generatePlan(id, [code]);
      draft.accept(r.plan);
      draft.setLocal(noEdits());
      const got = Array.isArray(r.kept) ? (r.kept as string[]) : [];
      setKept((prev) => [...prev.filter((c) => c !== code), ...got]);
      const cost = typeof r.cost === "number" ? ` · $${r.cost.toFixed(4)}` : "";
      setGenDone({ code, text: `Готово${cost}` });
    } catch (e) {
      const x = extra(e);
      setGenError({ scope, text: (x && GEN_TEXT[x.code]) || errorText(e) });
      await draft.refresh();
    } finally {
      setBusy(null);
      draft.invalidateOthers();
    }
  };

  const take = async (code: string, genId: number) => {
    setBusy({ kind: "take", code });
    setTakeError(null);
    try {
      await draft.drain();
      const base = draft.baseRef.current as PostDetail;
      draft.accept(await takeGen(id, code, genId, base.date_modify));
      draft.setLocal(noEdits());
      setKept((prev) => prev.filter((c) => c !== code));
    } catch (e) {
      if (!draft.markStale(e)) setTakeError({ code, text: errorText(e) });
    } finally {
      setBusy(null);
    }
  };

  /** `code` — сеть перехода (утверждение, возврат, отмена сети); `null` — переход всей публикации. */
  /** `true` — переход прошёл (удаление по нему уводит в список). */
  const changeState = async (
    call: (modify: string) => Promise<PostDetail>,
    code: PostNetworkCode | null = null,
  ): Promise<boolean> => {
    setBusy({ kind: "state" });
    setStateError(null);
    try {
      await draft.drain();
      const base = draft.baseRef.current as PostDetail;
      draft.accept(await call(base.date_modify));
      draft.setLocal(noEdits());
      return true;
    } catch (e) {
      if (!draft.markStale(e)) {
        const x = extra(e);
        setStateError({
          text: x?.code === "NOT_APPROVABLE" ? "Утвердить нельзя:" : errorText(e),
          problems: stateProblems(x?.problems, code),
          net: code,
        });
      }
      return false;
    } finally {
      setBusy(null);
    }
  };

  /** Полоса «Что публикуем»: порядок, удаление и добавление — та же дорожка состояния, что утверждение/отмена. */
  const moveMedia = (mediaId: number, dir: -1 | 1) =>
    void changeState((m) => reorderMedia(id, m, move(plan.media, mediaId, dir)));
  const removeOneMedia = (mediaId: number) => void changeState((m) => removeMedia(id, mediaId, m));
  const addMediaBatch = (variantIds: number[]) => {
    setAddOpen(false);
    void changeState((m) => addMedia(id, m, variantIds));
  };
  /** Рамка элемента карусели для сети (карточка сети, переключатель элементов). */
  const setCrop = (code: PostNetworkCode, mediaId: number, crop: Crop | null) =>
    void changeState((m) => setMediaCrop(id, mediaId, m, { [code]: crop }));

  const removal = deletePlan(id, plan.status, approvedNets);
  const cancel = async () => {
    if (!removal || !window.confirm(removal.confirm)) return;
    if (await changeState((m) => cancelPlan(id, m))) navigate("/posts");
  };

  // --- переходы ОДНОЙ сети (№285): соседей не касаются
  const approveNet = (code: PostNetworkCode) => void changeState((m) => approvePlan(id, m, [code]), code);
  const unapproveNet = (code: PostNetworkCode) => void changeState((m) => unapprovePlan(id, m, [code]), code);
  const cancelNet = (code: PostNetworkCode) => {
    const name = NETWORK_LABEL[code];
    if (window.confirm(`Не публиковать в ${name}? Сеть уйдёт из публикации, вернуть её будет нельзя.`)) {
      void changeState((m) => cancelPlan(id, m, [code]), code);
    }
  };

  const genCode = (code: string) => busy?.kind === "generate" && busy.code === code;

  const templates = [
    `общий v${plan.prompts.base ?? "?"}`,
    ...(active.includes(net as PostNetworkCode)
      ? [`${NETWORK_LABEL[net as PostNetworkCode]} v${plan.prompts[net] ?? "?"}`]
      : []),
  ].join(", ");

  const carousel: CarouselProps = {
    readOnly: sharedReadOnly,
    busy: busy !== null,
    note:
      isDraft && approvedNets.length > 0
        ? `Элементы и платье не меняются: утверждено (${networkNames(approvedNets)}) — верните сеть в черновик.`
        : null,
    onMove: moveMedia,
    onRemove: removeOneMedia,
    onAdd: () => setAddOpen(true),
  };

  const firstHandle = (code: string) =>
    plan.accounts.find((n) => n.code === code)?.accounts.find((a) => a.checked)?.handle ?? "";

  return (
    <div className="flex flex-col gap-4 p-5">
      <div className="flex flex-wrap items-center gap-3">
        <Link to={plan.status === "approved" ? "/posts?tab=approved" : "/posts"} className="text-[12.5px] text-fg-muted hover:text-fg">
          ← Публикации
        </Link>
        <h1 className="text-[16px] font-medium text-fg">Публикация №{plan.id}</h1>
        <span
          className={
            "rounded-[var(--radius-ui)] px-1.5 py-0.5 text-[11.5px] " +
            (plan.status === "approved" ? "bg-ok-bg text-ok" : plan.status === "cancelled" ? "bg-bg-subtle text-fg-faint" : "bg-bg-subtle text-fg-muted")
          }
        >
          {STATUS_LABEL[plan.status]}
        </span>
        {partial ? (
          <span
            className="rounded-[var(--radius-ui)] bg-warn-bg px-1.5 py-0.5 text-[11.5px] text-warn"
            data-annot="утверждено частично"
          >
            {partial}
          </span>
        ) : null}
        <span className="text-[11.5px] text-fg-faint">
          {plan.author_name ?? "—"}
          {plan.status === "approved" ? ` · утвердил ${plan.approver_name ?? "—"} ${when(plan.date_approve)}` : ""}
        </span>
        {isDraft ? (
          <span className="ml-auto text-[11.5px] text-fg-faint" aria-live="polite">
            {draft.saving ? "сохраняю…" : !isEmpty(draft.local) ? "есть несохранённое" : "сохранено"}
          </span>
        ) : null}
        {removal ? (
          <button
            type="button"
            className={`${btn} ${isDraft ? "" : "ml-auto"} text-err`}
            disabled={stateBusy}
            data-annot="удалить черновик"
            onClick={() => void cancel()}
          >
            <Trash2 size={13} /> {removal.label}
          </button>
        ) : null}
      </div>

      {draft.stale ? (
        <div className="flex flex-wrap items-center gap-2 rounded-[var(--radius-ui)] border border-warn/40 bg-warn-bg px-3 py-2 text-[12.5px] text-warn">
          Черновик правили в другой вкладке — ваши последние правки не сохранены.
          <button type="button" className={btn} onClick={() => draft.reread(draft.stalePlan)}>
            Перечитать
          </button>
        </div>
      ) : null}
      {draft.saveError && !draft.stale ? (
        <div className="flex flex-wrap items-center gap-2 rounded-[var(--radius-ui)] border border-err/30 bg-err-bg px-3 py-2 text-[12.5px] text-err">
          Не сохранено: {saveText(draft.saveError.error)}
          {draft.saveError.kind === "retry" ? (
            <button type="button" className={btn} onClick={() => void draft.flush().catch(noop)}>
              Повторить
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="grid gap-2">
        <div role="tablist" className="flex flex-wrap gap-1" data-annot="сети">
          {tabs.map((n) => {
            const status = plan.networks[n.code]?.status ?? "draft";
            return (
              <button
                key={n.code}
                type="button"
                role="tab"
                aria-selected={n.code === net}
                title={`${n.name}: ${NET_STATUS_LABEL[status]}`}
                onClick={() =>
                  setParams(
                    (prev) => {
                      const next = new URLSearchParams(prev);
                      next.set("net", n.code);
                      return next;
                    },
                    { replace: true },
                  )
                }
                className={
                  "rounded-[var(--radius-ui)] border px-3 py-1.5 text-[12.5px] " +
                  (n.code === net
                    ? "border-accent bg-accent-dim font-medium text-accent-text"
                    : "border-border bg-bg text-fg-muted hover:bg-bg-subtle")
                }
              >
                {n.name} · {counts[n.code] ?? 0}
                {status === "approved" ? (
                  <span className="ml-1 text-ok">✓</span>
                ) : status === "cancelled" ? (
                  <span className="ml-1 text-fg-faint">×</span>
                ) : null}
              </button>
            );
          })}
        </div>
        <AccountPicker plan={plan} readOnly={netReadOnly(net)} net={net} onChange={(e) => draft.edit(e, true)} />
        {stateError && stateError.net === net && cards.length === 0 ? (
          <div className="text-[12px] text-err">{stateError.text}</div>
        ) : null}
      </div>
      <CandidatePicker open={addOpen} mode="add" onClose={() => setAddOpen(false)} onAdd={addMediaBatch} />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,320px)]">
        <div className="grid min-w-0 content-start gap-4">
          {isDraft && cards.length === 0 ? (
            <div className="grid gap-3">
              <div className="text-[12px] text-fg-faint" data-annot="генерация текстов">
                {plan.networks[net]?.status === "cancelled"
                  ? "сеть отменена — в этой публикации не выкладываем"
                  : soleAccount(plan, net)
                    ? "включите публикацию в этой сети — появится её карточка с текстом"
                    : "отметьте аккаунты этой сети — появится её карточка с текстом"}
              </div>
              {/* состав карусели общий: правится и пока ни одна сеть не включена */}
              <CarouselBar
                media={plan.media}
                activeId={plan.media.some((m) => m.id === loosePick) ? loosePick : (plan.media[0]?.id ?? null)}
                onPick={setLoosePick}
                carousel={carousel}
              />
            </div>
          ) : null}

          {cards.map(({ code, net, format }) => (
            <NetworkCard
              key={code}
              code={code}
              net={net}
              format={format}
              gens={plan.gens[code] ?? []}
              media={plan.media}
              carousel={carousel}
              schedule={{
                planId: plan.id,
                value: net.publish_date,
                readOnly: plan.status === "cancelled" || working,
                onSave: (date) => void changeState((m) => schedulePlan(id, m, code, date), code),
              }}
              handle={firstHandle(code)}
              readOnly={netReadOnly(code)}
              generating={genCode(code)}
              kept={kept.includes(code)}
              done={genDone?.code === code ? genDone.text : null}
              estimate={plan.estimate}
              templates={templates}
              error={
                genError?.scope === code ? genError.text : takeError?.code === code ? takeError.text : null
              }
              problems={netApproveProblems(problems, code)}
              stateBusy={stateBusy}
              stateError={
                stateError?.net === code
                  ? [stateError.text, stateError.problems.join("; ")].filter(Boolean).join(" ")
                  : null
              }
              onEdit={(fields: NetEdit, immediate) => draft.edit({ networks: { [code]: fields } }, immediate)}
              onRegenerate={() => void generate(code)}
              onTake={(genId) => void take(code, genId)}
              onCropChange={(mediaId, crop) => setCrop(code, mediaId, crop)}
              onApprove={() => approveNet(code)}
              onUnapprove={() => unapproveNet(code)}
              onCancelNetwork={() => cancelNet(code)}
            />
          ))}

          {/* Утверждение живёт в карточках сетей (№285); здесь — вся публикация целиком, и только когда
              есть что сказать (№383: пустая строка-пояснение — «лишняя информация»). */}
          {plan.status === "cancelled" || approvedNets.length > 0 || stateError?.net === null ? (
            <div className="grid gap-2 border-t border-border pt-3" data-annot="утверждение">
              {plan.status === "cancelled" ? (
                <span className="text-[12.5px] text-fg-faint">Публикация отменена — только чтение.</span>
              ) : approvedNets.length > 0 ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[12.5px] text-fg-muted">
                    {plan.status === "approved"
                      ? "Все сети утверждены — правок нет."
                      : `Утверждено: ${networkNames(approvedNets)}. Остальные сети — кнопкой в их карточках.`}
                  </span>
                  <button
                    type="button"
                    className={btn}
                    disabled={stateBusy}
                    title="Вернуть в черновик все утверждённые сети"
                    onClick={() => void changeState((m) => unapprovePlan(id, m))}
                  >
                    Вернуть все в черновик
                  </button>
                </div>
              ) : null}
              {stateError && stateError.net === null ? (
                <div className="text-[12px] text-err">
                  {stateError.text}
                  {stateError.problems.length > 0 ? (
                    <ul className="mt-1 grid gap-0.5">
                      {stateError.problems.map((p) => (
                        <li key={p}>• {p}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
        <ProductPanel plan={plan} />
      </div>
    </div>
  );
}

export function PostPlanPage() {
  const { planId } = useParams();
  const id = Number(planId);
  if (!Number.isInteger(id) || id <= 0) {
    return (
      <div className="p-5 text-[12.5px] text-fg-faint">
        Нет такой публикации. <Link to="/posts">← Публикации</Link>
      </div>
    );
  }
  // ключ: смена адреса /posts/1 → /posts/2 — новая очередь сохранения, без остатков прошлой
  return <PlanScreen key={id} id={id} />;
}
